import type { BuilderDetailEnvelope } from '@/api/questionnaires/types';
import { BACKEND_URL } from '@/lib/constants/api';
import { apiFetch } from '@/lib/utils/apiFetch';
import type {
  AdminQuestionnaireTemplatesResponse,
  CreateAdminQuestionnaireTemplateBody,
  CreatedAdminQuestionnaireTemplateResponse,
  DeleteAdminQuestionnaireTemplatesBody,
  ToggleAdminQuestionnaireTemplateBody,
} from './types';

/**
 * Pure RPC over `/api/admin/questionnaires/templates`, the Retraced-admin twins
 * of the tenant template routes. Same services on the backend, gated on the
 * `admin-questionnaire:*` rights only `RETRACED_ADMIN` holds.
 */

const BASE = '/admin/questionnaires/templates';

/** The list. Takes a pre-built query string; paging, sort and filter live in the hook. */
export async function fetchAdminQuestionnaireTemplates(
  qs: string,
): Promise<AdminQuestionnaireTemplatesResponse> {
  return apiFetch<AdminQuestionnaireTemplatesResponse>(
    `${BASE}?${qs}`,
    {},
    BACKEND_URL,
  );
}

/** Creates an empty, inactive draft template. Answers 201 with the row in a `{ data }` envelope. */
export async function createAdminQuestionnaireTemplate(
  body: CreateAdminQuestionnaireTemplateBody,
): Promise<CreatedAdminQuestionnaireTemplateResponse> {
  return apiFetch<CreatedAdminQuestionnaireTemplateResponse>(
    BASE,
    { method: 'POST', body: JSON.stringify(body) },
    BACKEND_URL,
  );
}

/** The builder read: one template with its sections and questions. */
export async function fetchAdminTemplateBuilderDetail(
  id: string,
): Promise<BuilderDetailEnvelope> {
  return apiFetch<BuilderDetailEnvelope>(
    `${BASE}/${encodeURIComponent(id)}`,
    {},
    BACKEND_URL,
  );
}

/** The builder save. Same body as the tenant `PUT /api/questionnaires/:id/builder`. */
export async function saveAdminTemplateBuilderDetail(
  id: string,
  body: Record<string, unknown>,
): Promise<BuilderDetailEnvelope> {
  return apiFetch<BuilderDetailEnvelope>(
    `${BASE}/${encodeURIComponent(id)}`,
    { method: 'PUT', body: JSON.stringify(body) },
    BACKEND_URL,
  );
}

/** Publishes (`1`) or withdraws (`0`) a template. Answers 204. */
export async function toggleAdminQuestionnaireTemplate(
  id: string,
  body: ToggleAdminQuestionnaireTemplateBody,
): Promise<void> {
  await apiFetch<void>(
    `${BASE}/${encodeURIComponent(id)}`,
    { method: 'PATCH', body: JSON.stringify(body) },
    BACKEND_URL,
  );
}

/** Clones a template into a new inactive draft. Answers 201 with the copy in a `{ data }` envelope. */
export async function copyAdminQuestionnaireTemplate(
  id: string,
): Promise<CreatedAdminQuestionnaireTemplateResponse> {
  return apiFetch<CreatedAdminQuestionnaireTemplateResponse>(
    `${BASE}/${encodeURIComponent(id)}/copy`,
    { method: 'POST' },
    BACKEND_URL,
  );
}

/** Deletes every id given, all or nothing. Answers 204. */
export async function deleteAdminQuestionnaireTemplates(
  body: DeleteAdminQuestionnaireTemplatesBody,
): Promise<void> {
  await apiFetch<void>(
    BASE,
    { method: 'DELETE', body: JSON.stringify(body) },
    BACKEND_URL,
  );
}
